import React, { useState } from 'react';
import { Icon } from '../Icon/Icon';
import { Button } from '../Button/Button';
import { useToast } from '../Toast/Toast';
import { InvStageChip } from './InvShell';
import { InvLogModal } from './InvLogModal';
import { INV_DONT, INV_PATIENT } from '../../invisalign/mockData';
import type { InvStage } from '../../invisalign/types';

export interface InvAlignersCardProps {
  /** Which of the six stages to render. Defaults to the mock patient's stage. */
  stage?: InvStage;
  /** Link to the patient's Invisalign tab. */
  invisalignHref?: string;
  onBookConsult?: () => void;
  className?: string;
}

/**
 * The Aligners card (amendment 30 §4, board INV7Modal) inside the Calendar
 * appointment modal's Opportunities block — it replaces the old flat
 * "Ortho / Invisalign Opportunity" benefit row. The content changes per stage;
 * Record the answer / Log the conversation open the shared taps-only modal.
 * Reads the one mock patient (Orville, Ready) for the concrete fields.
 */
export function InvAlignersCard({ stage, invisalignHref, onBookConsult, className }: InvAlignersCardProps) {
  const p = INV_PATIENT;
  const st: InvStage = stage ?? p.stage;
  const { toast } = useToast();
  const [logOpen, setLogOpen] = useState(false);
  const [logMode, setLogMode] = useState<'before' | 'after'>('after');

  // "Asked — not now" and "Closed" show no card body.
  if (st === 'closed') return null;
  if (st === 'asked-not-now') {
    return (
      <div className={`px-[17px] py-3 text-[12.5px] text-[var(--text-muted)] ${className ?? ''}`}>
        Aligners · asked 12 Jun · not now · ask again after 12 Dec
      </div>
    );
  }

  const openLog = (mode: 'before' | 'after') => { setLogMode(mode); setLogOpen(true); };

  return (
    <div className={`overflow-hidden rounded-[12px] border border-[var(--brand-indigo-line)] bg-[var(--surface-card)] ${className ?? ''}`}>
      {/* header */}
      <div className="flex flex-wrap items-center gap-2 border-b border-[var(--border-default)] bg-[var(--brand-indigo-tint)] px-3.5 py-2.5">
        <Icon kind="check" size={15} stroke={2} style={{ color: 'var(--brand-indigo)' }} />
        <span className="text-[13px] font-bold text-[var(--text-primary)]">Aligners</span>
        <InvStageChip stage={st} />
        {invisalignHref !== undefined ? (
          <a href={invisalignHref} className="ml-auto text-[11.5px] font-semibold text-[var(--brand-indigo)] hover:underline">Invisalign tab →</a>
        ) : null}
      </div>

      <div className="px-3.5 py-3">
        {st === 'worth-asking' ? (
          <>
            <Line label="Why" body={`${p.whyProspect.map((s) => s.label).join(' · ')} · ${p.orthoBenefit} ortho benefit`} />
            <div className="mt-1 text-[12px] font-semibold text-[var(--text-secondary)]">Nobody has asked him yet.</div>
            <ScriptBox text="Your plan has orthodontic benefit you’ve never used. Have you ever thought about straightening your teeth? I can have someone send you a bit about clear aligners." />
          </>
        ) : null}

        {st === 'learning' ? (
          <>
            <Line label="Progress" body={`Day ${String(p.emails.day)} of ${String(p.emails.of)} · ${String(p.emails.opened)} opened · asked the Invis-Line once`} />
            <Chips items={[...p.smileProfile, ...p.concerns]} />
            <WhatToSay lines={p.whatToSay.slice(0, 2)} />
          </>
        ) : null}

        {st === 'ready' ? (
          <>
            <Line label="Why now" body={p.readyWhy} />
            <Chips items={[...p.smileProfile, ...p.concerns, ...(p.comingUpEvent !== undefined ? [p.comingUpEvent] : []), p.whoDecides, p.orthoBenefit]} />
            <WhatToSay lines={p.whatToSay} />
          </>
        ) : null}

        {st === 'consult-booked' || st === 'accepted' ? (
          <>
            <Line label="Consult" body="8 Oct · 2:00 pm · Dr. Carter" />
            <div className="mt-1 text-[12px] text-[var(--text-secondary)]">ELVA has stopped emailing him.</div>
            <Chips items={[...p.smileProfile, p.orthoBenefit]} />
            <div className="mt-2 rounded-[8px] bg-[var(--surface-sunken)] px-3 py-2 text-[12px] text-[var(--text-secondary)]">
              <span className="font-semibold text-[var(--text-primary)]">Before the consult · </span>eligibility on file, scanner ready, his questions on cost and how it looks.
            </div>
          </>
        ) : null}

        {/* the fixed Don't line — never for consult-booked's before-list? spec shows it on ask/learning/ready */}
        {st === 'worth-asking' || st === 'learning' || st === 'ready' ? (
          <div className="mt-2.5 rounded-[8px] bg-[var(--warn-amber-fill)] px-3 py-1.5 text-[11px] text-[var(--text-secondary)]">{INV_DONT}</div>
        ) : null}

        {/* eligibility — one green line (fix) */}
        <div className="mt-2.5 flex flex-wrap items-center gap-1.5 text-[11.5px]">
          <Icon kind="check" size={13} stroke={2.2} style={{ color: 'var(--money-green)' }} />
          <span className="font-semibold text-[var(--money-green)]">Eligibility checked today 8:42 am · Delta Dental PPO · active</span>
          <button type="button" className="text-[var(--brand-indigo)] hover:underline" onClick={() => { toast({ title: 'Eligibility details', variant: 'success' }); }}>Details</button>
        </div>

        {/* buttons */}
        <div className="mt-3 flex flex-wrap gap-2">
          {st === 'worth-asking' ? (
            <Button variant="primary" size="sm" onClick={() => { openLog('before'); }}>Record the answer</Button>
          ) : null}
          {st === 'ready' ? (
            <>
              <Button variant="primary" size="sm" iconLeft="calendar" onClick={() => { if (onBookConsult) { onBookConsult(); } else { toast({ title: 'Book consult — Aligner consult · 45 min · Dr. Carter', variant: 'success' }); } }}>Book consult</Button>
              <Button variant="secondary" size="sm" iconLeft="phone" onClick={() => { openLog('after'); }}>Log the conversation</Button>
            </>
          ) : null}
          {st === 'learning' ? (
            <Button variant="secondary" size="sm" iconLeft="phone" onClick={() => { openLog('after'); }}>Log the conversation</Button>
          ) : null}
        </div>
      </div>

      <InvLogModal
        open={logOpen}
        patientName={p.name}
        mode={logMode}
        stage={st}
        onClose={() => { setLogOpen(false); }}
        onSave={(sentence) => { setLogOpen(false); toast({ title: `Logged — ${sentence}`, variant: 'success' }); }}
      />
    </div>
  );
}

function Line({ label, body }: { label: string; body: string }) {
  return <div className="text-[12.5px] text-[var(--text-secondary)]"><span className="font-semibold text-[var(--text-muted)]">{label}: </span>{body}</div>;
}
function ScriptBox({ text }: { text: string }) {
  return (
    <div className="mt-2 rounded-[8px] bg-[var(--surface-sunken)] px-3 py-2 text-[12px] italic text-[var(--text-secondary)]">
      <span className="font-semibold not-italic text-[var(--text-muted)]">Ask · </span>{text}
    </div>
  );
}
function Chips({ items }: { items: string[] }) {
  return (
    <div className="mt-2 flex flex-wrap gap-1">
      {items.map((it, i) => <span key={`${it}-${String(i)}`} className="rounded-full border border-[var(--border-default)] px-2 py-0.5 text-[11px] text-[var(--text-secondary)]">{it}</span>)}
    </div>
  );
}
function WhatToSay({ lines }: { lines: { label: string; body: string }[] }) {
  return (
    <div className="mt-2.5">
      <div className="pc-eyebrow text-[var(--text-muted)]">What to say</div>
      <div className="mt-1 flex flex-col gap-1.5">
        {lines.map((w) => (
          <div key={w.label} className="grid grid-cols-[76px_1fr] gap-2 text-[12px]">
            <span className="font-semibold text-[var(--text-muted)]">{w.label}</span>
            <span className="text-[var(--text-secondary)]">{w.body}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
